import apiService from './api';

export interface Option {
  value: string;
  label: string;
}

class OptionsService {
  /**
   * Charger les véhicules
   */
  async getVehicules(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/camions/', { params: { page_size: 1000 } });
      return response.data.results?.map((v: any) => ({
        value: v.id.toString(),
        label: `${v.immatriculation} - ${v.marque} ${v.modele}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des véhicules:', error);
      return [];
    }
  }

  /**
   * Charger les remorques
   */
  async getRemorques(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/remorques/', { params: { page_size: 1000 } });
      return response.data.results?.map((r: any) => ({
        value: r.id.toString(),
        label: `${r.immatriculation} - ${r.marque} ${r.modele}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des remorques:', error);
      return [];
    }
  }

  /**
   * Charger les chauffeurs
   */
  async getChauffeurs(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/chauffeurs/', { params: { page_size: 1000 } });
      return response.data.results?.map((c: any) => ({
        value: c.id.toString(),
        label: `${c.prenom} ${c.nom} - ${c.telephone}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des chauffeurs:', error);
      return [];
    }
  }

  /**
   * Charger les convoyeurs
   */
  async getConvoyeurs(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/convoyeurs/', { params: { page_size: 1000 } });
      return response.data.results?.map((c: any) => ({
        value: c.id.toString(),
        label: `${c.nom} - ${c.telephone}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des convoyeurs:', error);
      return [];
    }
  }

  /**
   * Charger les clients
   */
  async getClients(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/clients/', { params: { page_size: 1000 } });
      return response.data.results?.map((c: any) => ({
        value: c.id.toString(),
        label: `${c.nom} - ${c.type}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      return [];
    }
  }

  /**
   * Charger les fournisseurs
   */
  async getFournisseurs(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/fournisseurs/', { params: { page_size: 1000 } });
      return response.data.results?.map((f: any) => ({
        value: f.id.toString(),
        label: `${f.nom} - ${f.cooperative}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des fournisseurs:', error);
      return [];
    }
  }

  /**
   * Charger les transporteurs
   */
  async getTransporteurs(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/transporteurs/', { params: { page_size: 1000 } });
      return response.data.results?.map((t: any) => ({
        value: t.id.toString(),
        label: `${t.nom} - ${t.type}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des transporteurs:', error);
      return [];
    }
  }

  /**
   * Charger les exportateurs
   */
  async getExportateurs(): Promise<Option[]> {
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await apiService.get('/weighing/api/exportateurs/', { params: { page_size: 1000 } });
      return response.data.results?.map((e: any) => ({
        value: e.id.toString(),
        label: `${e.nom} - ${e.pays}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des exportateurs:', error);
      return [];
    }
  }

  /**
   * Charger les magasins
   */
  async getMagasins(): Promise<Option[]> {
    try {
      const response = await apiService.get('/weighing/api/magasins/', { params: { page_size: 1000 } });
      return response.data.results?.map((m: any) => ({
        value: m.id.toString(),
        label: `${m.nom}${m.code ? ` (${m.code})` : ''}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des magasins:', error);
      return [];
    }
  }

  /**
   * Charger les conditionnements
   */
  async getConditionnements(): Promise<Option[]> {
    try {
      const response = await apiService.get('/weighing/api/conditionnements/', { params: { page_size: 1000 } });
      return response.data.results?.map((c: any) => ({
        value: c.id.toString(),
        label: c.nom || c.code || `Conditionnement #${c.id}`
      })) || [];
    } catch (error) {
      console.error('Erreur lors du chargement des conditionnements:', error);
      return [];
    }
  }

  /**
   * Charger toutes les options en parallèle
   */
  async getAllOptions() {
    try {
      const [
        vehicules,
        remorques,
        chauffeurs,
        convoyeurs,
        clients,
        fournisseurs,
        transporteurs,
        exportateurs,
        magasins,
        conditionnements
      ] = await Promise.all([
        this.getVehicules(),
        this.getRemorques(),
        this.getChauffeurs(),
        this.getConvoyeurs(),
        this.getClients(),
        this.getFournisseurs(),
        this.getTransporteurs(),
        this.getExportateurs(),
        this.getMagasins(),
        this.getConditionnements()
      ]);

      return {
        vehicules,
        remorques,
        chauffeurs,
        convoyeurs,
        clients,
        fournisseurs,
        transporteurs,
        exportateurs,
        magasins,
        conditionnements
      };
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
      return {
        vehicules: [],
        remorques: [],
        chauffeurs: [],
        convoyeurs: [],
        clients: [],
        fournisseurs: [],
        transporteurs: [],
        exportateurs: [],
        magasins: [],
        conditionnements: []
      };
    }
  }
}

export default new OptionsService();
